/**
 * Shared pagination types for every list endpoint.
 *
 * Backend list responses use the envelope:
 *   { success: true, data: { items: T[], pagination: PaginationMeta } }
 *
 * The SDK strips the outer `{ success, data }` wrapper, so consumers receive
 * `Paginated<T>` directly.
 */

export interface PaginationMeta {
  page: number;
  perPage: number;
  total: number;
  totalPages: number;
}

export interface Paginated<T> {
  items: T[];
  pagination: PaginationMeta;
}

export interface PaginationParams {
  page?: number;
  perPage?: number;
}

export const DEFAULT_PER_PAGE = 50;

/**
 * Encode pagination params + arbitrary filter values into a query string.
 * `undefined`/`null` filter values are skipped so callers can build a
 * filters object once and let the helper omit empty fields.
 */
export function buildListQuery(
  params: PaginationParams,
  filters: Record<string, string | number | boolean | undefined | null> = {},
): string {
  const search = new URLSearchParams();
  if (params.page !== undefined) search.set('page', String(params.page));
  if (params.perPage !== undefined) search.set('perPage', String(params.perPage));
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

export function emptyPage<T>(perPage = DEFAULT_PER_PAGE): Paginated<T> {
  return {
    items: [],
    pagination: { page: 1, perPage, total: 0, totalPages: 0 },
  };
}
